<script>
/**
 * O menu do "+" do composer: as ferramentas que a IA sabe usar.
 *
 * É o painel que o ChatGPT abre no "+". Cada linha é um MÓDULO do escritório
 * (CRM, Consolidador, Tarefas…); escolher um põe o chip na caixa e faz aparecer,
 * abaixo dela, o carrossel com as ações daquele módulo.
 *
 * ## O anexo é a primeira linha, e continua desabilitado
 *
 * O "+" era o gatilho de anexo. Em vez de perder essa porta, o anexo entra como
 * a primeira opção do menu — o mesmo lugar em que o ChatGPT põe "Adicionar
 * fotos e arquivos". Desabilitado, com o motivo na descrição: sumir com ele
 * deixaria o usuário sem saber que a função existe.
 *
 * ## Lista PLANA, sem cabeçalhos de seção
 *
 * Cada linha já é um módulo, então não há o que agrupar. É o que permite reusar
 * o `composer-inline-select` sem estendê-lo: ele já faz painel, navegação por
 * teclado, clique-fora e abertura para baixo.
 *
 * Não lê store nem `$access`: recebe a lista pronta de quem sabe quais módulos
 * este escritório enxerga (`acoesVisiveis`, na superfície hospedeira).
 *
 * Sem `?.`/`??` (o Babel deste projeto não transpila).
 */
import ComposerInlineSelect from '@modules/aawz-intelligence-v2/components/composer-inline-select/composer-inline-select.vue'
import { normalizeText } from '@utils/Format'

const ICONE_DO_ANEXO = 'paperclip'

const SEM_RESULTADO = 'Nenhuma ferramenta com esse nome'

// Dica do pé do painel, em repouso. Diz o que dá para BUSCAR, e por isso muda
// com o modo: pela `/` a lista tem comandos além das ferramentas, pelo "+" não.
const RODAPE_FERRAMENTAS = 'Digite para buscar ferramentas do escritório'
const RODAPE_COMANDOS = 'Digite para buscar comandos e ferramentas'

// O "+" é o único conteúdo do botão — 13px (o padrão dos chips com rótulo)
// deixava um ícone perdido dentro de um círculo de 34.
const TAMANHO_DO_MAIS = 20

// Comparação sem acento e sem caixa — o usuário digita corrido. `normalizeText`
// (`@utils/Format`) é o util GLOBAL do repo pra isto — a mesma função que
// `view-chat.vue` reusa no filtro ao vivo da lista fixa de módulos do
// composer, pra não duas normalizações de texto divergirem no dia em que uma
// delas mudar.
const semAcento = (texto) => normalizeText(texto || '')

// A barra que abre o menu (`COMANDO_DE_FERRAMENTAS` em `send-message.vue`).
const BARRA_DO_COMANDO = '/'

/**
 * Tira a barra INICIAL do termo antes de compará-lo com o filtro.
 *
 * Os dois lados da comparação falavam alfabetos diferentes: `filtro` é, por
 * contrato, "o texto digitado DEPOIS da `/`" (a prop abaixo se documenta
 * assim, e `send-message.vue::filtroDeFerramentas` corta a barra antes de
 * passar) — mas o RÓTULO de um comando pode trazê-la: o catálogo de atalhos
 * vem do servidor com `label: '/panorama'` (`core/atalhos/catalogo.py::
 * Atalho.para_payload()`). Como `'/panorama'.indexOf('panorama')` é 1 e não
 * 0, quem digitava `/panorama` — exatamente o rótulo que este menu exibe —
 * recebia "Nenhuma ferramenta com esse nome" (defeito reproduzido na tela,
 * spec-013 T022). Os 8 comandos antigos escapavam por acaso: "Nova
 * conversa", "Agente" e companhia não começam com `/`.
 *
 * Normalizar o termo aqui — e não tirar a barra do `label` — porque a barra
 * é o que o contrato manda e o que o usuário lê na linha do menu.
 */
const semBarra = (texto) => {
  const valor = String(texto || '')
  if (valor.indexOf(BARRA_DO_COMANDO) !== 0) return valor
  return valor.slice(BARRA_DO_COMANDO.length)
}

/**
 * ONDE o termo casou num item — e é a ordem em que ele aparece na lista.
 *
 * ★ [owner, 21/09/2026] A busca casava só por PREFIXO do rótulo: *"ao
 * pesquisar 'tri' não aparece /fechamento-trimestral; ele só acha se começar
 * certinho"*. Com o catálogo de atalhos do servidor a lista deixou de ter
 * seis linhas, e o que o consultor lembra costuma ser o MEIO do slug
 * (`trimestral`, `migrar`) ou uma palavra da descrição. Agora casa por
 * TRECHO no rótulo (e apelidos) e na descrição — mas quem casa pelo começo
 * do rótulo continua na frente: `/pla` mostra "Planejamento" antes de
 * qualquer descrição que só contenha "pla".
 */
const FAIXA_INICIO_DO_ROTULO = 0
const FAIXA_MEIO_DO_ROTULO = 1
const FAIXA_DESCRICAO = 2
const NAO_CASOU = -1

const faixaDoTermo = (alvo, rotulos, descricao) => {
  const posicoes = rotulos.map((rotulo) => semAcento(semBarra(rotulo)).indexOf(alvo))
  if (posicoes.indexOf(0) !== -1) return FAIXA_INICIO_DO_ROTULO
  if (posicoes.some((posicao) => posicao > 0)) return FAIXA_MEIO_DO_ROTULO
  if (semAcento(descricao).indexOf(alvo) !== -1) return FAIXA_DESCRICAO
  return NAO_CASOU
}

// Filtra e ordena por faixa, preservando a ordem original DENTRO de cada uma
// (partição à mão, e não `sort`: a estabilidade do sort não é garantida em
// todo navegador que o repo ainda serve).
const filtrarPorTermo = (itens, alvo, rotulosDe, descricaoDe) => {
  const faixas = [[], [], []]
  itens.forEach((item) => {
    const faixa = faixaDoTermo(alvo, rotulosDe(item), descricaoDe(item))
    if (faixa !== NAO_CASOU) faixas[faixa].push(item)
  })
  return faixas[FAIXA_INICIO_DO_ROTULO].concat(faixas[FAIXA_MEIO_DO_ROTULO], faixas[FAIXA_DESCRICAO])
}


export default {
  name: 'IntelligenceV2ComposerFerramentas',
  components: { ComposerInlineSelect },
  props: {
    // `[{ moduleKey, icone, rotulo, acoes: [...] }]` — saída de
    // `acoesVisiveis()`, já recortada nos módulos do escritório.
    modulos: {
      type: Array,
      default: function () {
        return []
      },
    },
    // Módulo já escolhido, para a linha aparecer marcada ao reabrir o menu.
    moduleKeyAtivo: {
      type: String,
      default: '',
    },
    rotuloDoAnexo: {
      type: String,
      default: '',
    },
    anexoIndisponivel: {
      type: Boolean,
      default: false,
    },
    // Texto digitado depois da `/` no composer. Filtra os módulos pelo nome —
    // `/crm` deixa só o CRM. Vazio = lista inteira.
    filtro: {
      type: String,
      default: '',
    },
    mostraAnexo: {
      type: Boolean,
      default: false,
    },
    /**
     * Comandos do composer — `[{ label, description, icon, aliases, onClick }]`.
     *
     * Só existem no caminho da `/`; o "+" continua sendo o menu de FERRAMENTAS
     * e nada mais. Quem monta é o `send-message`, que é quem tem as ações
     * (abrir outro menu, começar conversa nova, alternar raciocínio).
     */
    comandos: {
      type: Array,
      default: function () {
        return []
      },
    },
    // Menu aberto pela `/`, e não pelo "+". Muda o que a lista mostra —
    // ver `opcoes`.
    modoComando: {
      type: Boolean,
      default: false,
    },
    // [feedback do usuário, 04/09 — pivô ChatGPT] Enquanto grava (Ditar/Modo
    // de Voz), o "+" continua NO MESMO LUGAR — só fica desabilitado, em vez
    // de sumir/deslocar (era o defeito: o espectro sem `order` empurrava os
    // outros botões da fileira).
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return { TAMANHO_DO_MAIS }
  },
  computed: {
    /**
     * Sem acento e por TRECHO, no rótulo e na descrição: quem digita `/plan`
     * espera "Planejamento Financeiro", quem digita `/comiss` espera
     * "Comissões" mesmo sem o til — e quem digita `/tri` espera o módulo
     * cuja descrição fala de trimestre. Prefixo do rótulo vem primeiro
     * (`faixaDoTermo`).
     */
    modulosFiltrados() {
      const alvo = semAcento(this.filtro)
      if (!alvo) return this.modulos
      return filtrarPorTermo(
        this.modulos,
        alvo,
        (modulo) => [modulo.rotulo],
        (modulo) => modulo.descricao
      )
    },
    /**
     * Mesma regra dos módulos, estendida aos APELIDOS: quem vem de outro chat
     * digita `/new` ou `/clear` esperando conversa nova, e `/agent` em vez de
     * `/agente`. O rótulo em PT continua casando.
     *
     * ★ O termo passa por `semBarra` antes da comparação — rótulo que já
     * compõe a barra (`/panorama`, vindo do catálogo de atalhos do servidor)
     * jamais casaria com um filtro que, por contrato, chega sem ela. Ver o
     * comentário de `semBarra`, acima.
     */
    comandosFiltrados() {
      const alvo = semAcento(this.filtro)
      if (!alvo) return this.comandos
      return filtrarPorTermo(
        this.comandos,
        alvo,
        (comando) => [comando.label].concat(comando.aliases || []),
        (comando) => comando.description
      )
    },
    /**
     * Duas listas, e o que aparece depende de COMO o menu foi aberto.
     *
     * Pelo "+": as ferramentas, como sempre — anexo no topo e os módulos abaixo.
     *
     * Pela `/`: só os COMANDOS enquanto nada foi digitado, e os módulos entram
     * assim que houver filtro. É o comportamento dos outros chats: a barra
     * sozinha mostra o que dá pra fazer; digitar é que procura no resto.
     */
    opcoes() {
      const lista = []

      if (this.modoComando) {
        this.comandosFiltrados.forEach((comando) => lista.push(comando))
        // Sem filtro, os módulos ficam de fora — a `/` sozinha é a vitrine de
        // comandos, não a lista inteira.
        if (this.filtro) this.adicionarModulos(lista)
        return this.comFallback(lista)
      }

      // O anexo some quando há filtro: ele não é módulo, e mantê-lo no topo de
      // uma lista de um item só faria a primeira seta cair nele.
      if (this.mostraAnexo && !this.filtro) {
        lista.push({
          label: 'Adicionar arquivos',
          description: this.rotuloDoAnexo,
          icon: ICONE_DO_ANEXO,
          disabled: this.anexoIndisponivel,
          onClick: () => this.$emit('anexar'),
        })
      }

      this.adicionarModulos(lista)
      return this.comFallback(lista)
    },
    // ⚠️ Gate no que EXISTE, não no que o filtro deixou passar. Com
    // `opcoes.length` aqui, digitar `/xpto` esvaziava a lista e desmontava o
    // componente inteiro — o "+" sumia da caixa junto com o menu.
    temFerramentas() {
      return this.modulos.length > 0
    },
    // Some assim que se digita: a dica manda DIGITAR, e continuar dizendo isso
    // com a busca em andamento seria instruir a fazer o que já está feito.
    rodape() {
      if (this.filtro) return ''
      return this.modoComando ? RODAPE_COMANDOS : RODAPE_FERRAMENTAS
    },
  },
  methods: {
    // `descricao` do MÓDULO, e não a contagem de ações que estava aqui: "4
    // ações" não ajudava a escolher — quem abre o menu procura pelo que o módulo
    // FAZ, e o número só competia com o nome em toda linha.
    adicionarModulos(lista) {
      this.modulosFiltrados.forEach((modulo) => {
        lista.push({
          label: modulo.rotulo,
          description: modulo.descricao || '',
          icon: modulo.icone,
          active: modulo.moduleKey === this.moduleKeyAtivo,
          onClick: () => this.$emit('escolher', modulo),
        })
      })
    },
    // Nada casou: uma linha desabilitada dizendo isso, em vez de painel vazio.
    // Painel em branco parece defeito; a frase explica.
    comFallback(lista) {
      if (lista.length) return lista
      return [{ label: SEM_RESULTADO, disabled: true }]
    },
    // Repassa para o dropdown. Existe porque o composer abre este menu pelo
    // atalho `/`, e ele não deve alcançar o filho por dentro.
    abrir(comFoco) {
      const select = this.$refs.select
      if (select) select.abrir(comFoco)
    },
    fechar() {
      const select = this.$refs.select
      if (select) select.fechar()
    },
    // Lido pelo `algumMenuAberto` do composer (vale para o "+" e para a `/`).
    isOpen() {
      const select = this.$refs.select
      return Boolean(select && select.isOpen())
    },
  },
}
</script>

<template>
  <!-- ⚠️ `icon-color` FIXO e não herdado. Sem ele o gatilho assume o
       `activeColor` assim que alguma opção está marcada, e o "+" virava roxo
       depois de escolher um módulo — ele é o botão que ABRE o menu, não um
       indicador do que está escolhido. -->
  <!-- `direction="auto"` e não um lado fixo: o composer fica no MEIO da tela
       enquanto não há conversa e desce para o rodapé depois da primeira
       mensagem. Com `down` fixo o menu saía pela base assim que o chat
       começava. -->
  <ComposerInlineSelect
    v-if="temFerramentas"
    ref="select"
    class="composer-ferramentas"
    direction="auto"
    largura-total
    active-color="var(--primary)"
    icon-color="var(--neutral-700)"
    label="Ferramentas"
    icon="plus-line"
    so-icone
    manter-foco
    :tamanho-do-icone="TAMANHO_DO_MAIS"
    :options="opcoes"
    :rodape="rodape"
    :disabled="disabled"
    v-on="$listeners"
  />
</template>

<style lang="sass" scoped>
// O gatilho é SÓ o "+": círculo de 34px, a mesma régua do enviar, do agente e
// do escopo. `so-icone` já tira o rótulo e o chevron — aqui fica o tamanho.
//
// ★ 34px: mudou aqui, mude nos outros quatro (`grep -rn "34px e não os 30"`).
.composer-ferramentas ::v-deep .composer-inline-select__trigger
  width: 34px
  height: 34px
  padding: 0
  justify-content: center
  border-radius: 50%
  background: transparent
  color: var(--neutral-700)

  // Mesmo hover de `agent-select.vue`/`escopo-select.vue` (--neutral-200) —
  // os três "só-ícone" do composer precisam ler como a MESMA família.
  &:hover
    background: var(--neutral-200)

// ⚠️ SEM realce de "aberto" no gatilho. O menu também abre pelo comando `/`
// digitado no campo — e aí o botão aceso dizia que o foco estava nele, quando
// o cursor está no texto. Quem anuncia que há menu aberto é o próprio painel.
.composer-ferramentas ::v-deep .composer-inline-select__trigger--open
  background: transparent
</style>
